import React, { useState } from "react";
import { useAuth, ROLE_LABEL } from "@/context/AuthContext";
import { SectionCard } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogOut, KeyRound, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

function passwordChecks(pw) {
  return {
    length: pw.length >= 8,
    hasLetter: /[A-Za-z]/.test(pw),
    hasDigit: /\d/.test(pw),
  };
}

function CheckItem({ ok, children }) {
  return (
    <div className={`flex items-center gap-1.5 text-xs ${ok ? "text-emerald-600" : "text-slate-500"}`}>
      <CheckCircle2 size={12} className={ok ? "opacity-100" : "opacity-30"} />
      {children}
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const checks = passwordChecks(next);
  const allChecks = checks.length && checks.hasLetter && checks.hasDigit;
  const canSubmit = current && next && confirm && allChecks && next === confirm;

  async function submit(e) {
    e.preventDefault();
    if (next !== confirm) return toast.error("New password and confirmation do not match.");
    if (!allChecks) return toast.error("Password does not meet all requirements.");
    setLoading(true);
    try {
      await api.post("/profile/change-password", { current_password: current, new_password: next });
      toast.success("Password changed successfully");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }

  return (
    <SectionCard title="Change Password" subtitle="Password must be at least 8 characters and combine letters + numbers.">
      <form onSubmit={submit} className="space-y-3 max-w-md">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Current password</Label>
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
            data-testid="current-password-input" className="mt-1 h-10" autoComplete="current-password" required />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">New password</Label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
            data-testid="new-password-input" className="mt-1 h-10" autoComplete="new-password" required />
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-1">
            <CheckItem ok={checks.length}>Min. 8 characters</CheckItem>
            <CheckItem ok={checks.hasLetter}>Contains letters</CheckItem>
            <CheckItem ok={checks.hasDigit}>Contains numbers</CheckItem>
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Confirm new password</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            data-testid="confirm-password-input" className={`mt-1 h-10 ${confirm && next !== confirm ? "border-red-300" : ""}`}
            autoComplete="new-password" required />
          {confirm && next !== confirm && <div className="text-[11px] text-red-600 mt-1">Passwords do not match</div>}
        </div>
        <Button type="submit" disabled={!canSubmit || loading}
          data-testid="change-password-submit"
          className="bg-sams-primary hover:bg-sams-primaryHover text-white">
          <KeyRound size={14} className="mr-2" /> {loading ? "Changing..." : "Change Password"}
        </Button>
      </form>
    </SectionCard>
  );
}

export default function AccountPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const initials = (user?.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">My Account</div>
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">Profile</h1>
      </div>
      <SectionCard title="Account details" subtitle="Your account information">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-md bg-slate-900 text-white grid place-items-center font-heading font-black text-2xl">{initials}</div>
          <div>
            <div className="font-heading text-xl font-bold text-slate-900">{user?.name}</div>
            <div className="text-sm text-slate-600">{user?.email}</div>
            <div className="text-xs uppercase tracking-wider text-sams-primary font-semibold mt-1">{ROLE_LABEL[user?.role]}</div>
          </div>
        </div>
        <div className="mt-6">
          <Button variant="outline" onClick={async () => { await logout(); nav("/login"); }} data-testid="account-logout-button">
            <LogOut size={14} className="mr-2" /> Sign out
          </Button>
        </div>
      </SectionCard>

      <ChangePasswordCard />
    </div>
  );
}
